/**
 * ComposerRecipients — the composer's "To" (conversation-members spec): who the next message is for, always shown,
 * because every AI member that receives a message runs a model cycle. With no pick it is the default — the member whose
 * question the person has not answered yet, else the lead, else no one (people talking among themselves run nothing).
 * A pick names members by address; "@" in the message adds to it. Built on `ToolbarPopover` (demo
 * `/ui-elements/popovers`), its rows copied from the mention panel.
 */
import {
  type ConversationMemberRef,
  isAiMemberType,
  MAX_AI_RECIPIENTS,
  memberKindLabel,
} from '@confluent/shared/lib/conversation-addressing';
import { AtSign, Check, UserPlus } from 'lucide-react';
import { Can } from '../auth/Can.js';
import { Button } from '../ui/Button.js';
import { ToolbarPopover } from '../ui/ToolbarPopover.js';
import { canPickAi, type DefaultRecipient, togglePick } from './conversation-members-model.js';

interface ComposerRecipientsProps {
  /** Everyone the person can address (never the person themselves). */
  members: readonly ConversationMemberRef[];
  /** The pick; null keeps the default. */
  chosen: readonly number[] | null;
  fallback: DefaultRecipient;
  onChange: (chosen: number[] | null) => void;
  /** Opens "Add a member" (people who manage agents). */
  onAddMember?: () => void;
}

/** What the trigger reads. */
export function recipientsLabel(
  chosen: readonly number[] | null,
  members: readonly ConversationMemberRef[],
  fallback: DefaultRecipient,
): string {
  if (chosen && chosen.length > 0) {
    const names = chosen.map((id) => members.find((member) => member.id === id)?.name ?? `Member ${id}`);
    return `To: ${names.join(', ')}`;
  }
  if (fallback.kind === 'answering') return `To: ${fallback.member.name} (answering)`;
  if (fallback.kind === 'lead') return `To: ${fallback.member.name}`;
  return 'To: no one';
}

function fallbackNote(fallback: DefaultRecipient): string {
  if (fallback.kind === 'answering')
    return `${fallback.member.name} asked you something; your next message answers it.`;
  if (fallback.kind === 'lead') return `With no one picked, the message goes to the lead, ${fallback.member.name}.`;
  return 'With no one picked, the people here read the message and no AI member runs.';
}

export function ComposerRecipients({ members, chosen, fallback, onChange, onAddMember }: ComposerRecipientsProps) {
  const picked = chosen ?? [];
  const aiRoom = canPickAi(chosen, members);
  return (
    <ToolbarPopover icon={AtSign} label="Who this message is for" value={recipientsLabel(chosen, members, fallback)}>
      {(close) => (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted">
            {picked.length > 0
              ? `Only the members picked receive the message. At most ${MAX_AI_RECIPIENTS} AI members at once.`
              : fallbackNote(fallback)}
          </p>
          <div className="flex flex-col" role="group" aria-label="Members">
            {members.map((member) => {
              const selected = picked.includes(member.id);
              const blocked = !selected && isAiMemberType(member.memberType) && !aiRoom;
              return (
                <Button
                  key={member.id}
                  variant="ghost"
                  aria-pressed={selected}
                  disabled={blocked}
                  title={blocked ? `At most ${MAX_AI_RECIPIENTS} AI members per message.` : undefined}
                  onClick={() => {
                    const next = togglePick(chosen, member, members);
                    onChange(next.length > 0 ? next : null);
                  }}
                  className="h-auto w-full justify-start gap-2 px-2 py-1.5 text-left font-normal"
                >
                  <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
                    {selected && <Check size={14} className="text-success" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-heading">{member.name}</span>
                  <span className="shrink-0 text-[11px] text-muted">
                    {member.role === 'lead'
                      ? `${memberKindLabel(member.memberType)} · lead`
                      : memberKindLabel(member.memberType)}
                  </span>
                </Button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
            {picked.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
                Back to the default
              </Button>
            ) : (
              <span />
            )}
            {onAddMember && (
              <Can policy="CanManageAgents">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    close();
                    onAddMember();
                  }}
                >
                  <UserPlus size={14} aria-hidden />
                  Add a member…
                </Button>
              </Can>
            )}
          </div>
        </div>
      )}
    </ToolbarPopover>
  );
}
